import { useContext, useEffect, useMemo, useState, useTransition } from "react";
import { useFormContext } from "react-hook-form";

import {
  IDropdownItemRideTypes,
  IRideDto,
  parseBoolean,
  RideFormFields,
  RideStationPanelMode,
  RideTypeCategoryEnum,
  useHasPermissions,
} from "@ryde/shared";

import { DropdownSourceContext } from "../../contexts";

export const useRideEmptyStationsAllowed = (
  mode: RideStationPanelMode | undefined,
  rideDto: Partial<IRideDto> | undefined,
) => {
  const { getDropdownItems } = useContext(DropdownSourceContext);
  const { hasPermissions: hasPermissionToGetRideTypes } = useHasPermissions({
    entity: "rideType",
    action: "list",
  });
  const [rideTypes, setRideTypes] = useState<IDropdownItemRideTypes[]>([]);
  const { watch } = useFormContext() || {};
  const [, startTransition] = useTransition();
  const isFormMode = !mode || mode === "CRUD_TAB";

  useEffect(() => {
    const getRideTypes = async (): Promise<void> => {
      const items = await getDropdownItems?.("rideType");

      setRideTypes(items || []);
    };

    if (isFormMode && hasPermissionToGetRideTypes) {
      startTransition(() => {
        getRideTypes();
      });
    }
  }, [getDropdownItems, hasPermissionToGetRideTypes, isFormMode]);

  const rideTypeId = watch?.(RideFormFields.rideTypeId) || rideDto?.rideTypeId;

  const rideTypeForSelectedId = useMemo(() => {
    if (!rideTypeId || !rideTypes?.length) {
      return undefined;
    }
    return rideTypes.find(x => x.value === rideTypeId);
  }, [rideTypeId, rideTypes]);

  const isRideWithEmptyStationsAllowed = parseBoolean(
    rideTypeForSelectedId?.isRideWithEmptyStationsAllowed,
  );
  const skipSettingSupplierByAreaGroup = parseBoolean(
    rideTypeForSelectedId?.skipSettingSupplierByAreaGroup,
  );
  const isRideWithDuplicatedStationsAllowed = parseBoolean(
    rideTypeForSelectedId?.isRideWithDuplicatedStationsAllowed,
  );
  const rideTypeCategory =
    rideTypeForSelectedId?.rideTypeCategory ?? RideTypeCategoryEnum.Normal;

  if (!isFormMode) {
    // for grid, computed values are injected on BE to dto itself
    const category = rideDto?.rideTypeCategory || RideTypeCategoryEnum.Normal;
    return {
      isRideWithEmptyStationsAllowed: parseBoolean(
        rideDto?.isRideWithEmptyStationsAllowed,
      ),
      skipSettingSupplierByAreaGroup: parseBoolean(
        rideDto?.skipSettingSupplierByAreaGroup,
      ),
      isRideWithDuplicatedStationsAllowed: parseBoolean(
        rideDto?.isRideWithDuplicatedStationsAllowed,
      ),
      rideTypeCategory: category,
      isRideHasLockedEndDate: category === RideTypeCategoryEnum.LockedEndDate,
      isRideHasIndependentDayAdjustment:
        category === RideTypeCategoryEnum.IndependentDayAdjustment ||
        rideDto?.metadata?.hasIndependentDayAdjustment,
    };
  }

  const isRideHasLockedEndDate =
    rideTypeCategory === RideTypeCategoryEnum.LockedEndDate;

  const isRideHasIndependentDayAdjustment =
    rideTypeCategory === RideTypeCategoryEnum.IndependentDayAdjustment;

  return {
    isRideWithEmptyStationsAllowed,
    skipSettingSupplierByAreaGroup,
    isRideWithDuplicatedStationsAllowed,
    rideTypeCategory,
    isRideHasLockedEndDate,
    isRideHasIndependentDayAdjustment,
  };
};
